import { FC, useMemo } from "react";
import { Stack } from "@mui/material";
import formatNumber from "../../../utils/formatNumber";
import { calcRoi, formatCurrency, printAPR } from "../../../utils/utils";
import { Strategy } from "../../../store/slices/strategySlice";
import { _roundDec } from "../../../utils/_roundToDecimals";
import DottedTextField from "../../../_components/DottedTextField";
import PaperPosition from "./PaperPosition";
import { precision } from "../../../_utils/precision";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import PaperCollapse from "../../../_components/PaperCollapse";
import Text from "../../../_components/Text";

interface AutoStrategyPerformanceInfoProps {
  strategy: Strategy;
}

const AutoStrategyPerformanceInfo: FC<AutoStrategyPerformanceInfoProps> = ({ strategy }) => {
  const tickers = useSelector((state: RootState) => state.shared.tickers);

  const tickerPriceStep = useMemo(() => {
    let foundPriceStep = tickers.find(
      (ticker) => ticker.base === strategy.base && ticker.quote === strategy.quote
    )?.priceStep;

    if (foundPriceStep) {
      return precision(foundPriceStep);
    } else {
      return 4;
    }
  }, [strategy.base, strategy.quote, tickers]);

  const totalPosition = useMemo(() => {
    return _roundDec(
      strategy.currentOpenPositionAmount + strategy.currentStrategyPositionAmount,
      tickerPriceStep
    );
  }, [strategy.currentOpenPositionAmount, strategy.currentStrategyPositionAmount]);

  const totalAvgPositionPrice = useMemo(() => {
    if (strategy.currentOpenPositionAmount + strategy.currentStrategyPositionAmount === 0) return 0;
    else {
      return _roundDec(
        (strategy.currentOpenPositionAmount * strategy.avgPositionOpeningPrice +
          strategy.currentStrategyPositionAmount * strategy.avgStrategyPositionPrice) /
          (strategy.currentOpenPositionAmount + strategy.currentStrategyPositionAmount),
        2
      );
    }
  }, [
    strategy.currentOpenPositionAmount,
    strategy.avgPositionOpeningPrice,
    strategy.avgStrategyPositionPrice,
    strategy.currentStrategyPositionAmount,
  ]);

  return (
    <Stack width="100%">
      <Text variant="h2" mb="16px">
        Performance
      </Text>

      <Stack width="100%" direction="column" gap="8px">
        <Text variant="h3" paddingBottom="16px">
          Total
        </Text>

        <DottedTextField
          placeholder={"Total fees"}
          value={`${formatCurrency(formatNumber(strategy.totalFees), strategy.quote)}`}
        />
        <DottedTextField
          placeholder={"Total realized profit "}
          value={`${formatCurrency(formatNumber(strategy.totalRealizedPnl), strategy.quote)}`}
        />

        <PaperPosition
          valuePosition={totalPosition}
          avgPosition={totalAvgPositionPrice}
          base={strategy.base}
          quote={strategy.quote}
          unrealizedPnl={strategy.totalUnrealizedPnl}
        />

        <DottedTextField
          placeholder={"Total PnL"}
          value={`${formatCurrency(formatNumber(strategy.totalRealizedPnl + strategy.totalUnrealizedPnl), strategy.quote)}`}
        />
        <DottedTextField
          placeholder={"ROI"}
          value={`
              ${formatNumber(
                calcRoi(
                  strategy.totalRealizedPnl + strategy.totalUnrealizedPnl,
                  strategy.investmentQuote,
                  strategy.leverage
                ) * 100
              )}%`}
        />
        <DottedTextField fullWidth placeholder={"APR"} value={printAPR(strategy.apr)} />

        <Stack direction="column">
          <PaperCollapse label={"Bots"} collapsed={false}>
            <Stack width="100%" direction="column" gap="8px">
              <DottedTextField
                placeholder={"Grid profit"}
                value={`${formatCurrency(formatNumber(strategy.gridProfit), strategy.quote)}`}
              />
              <DottedTextField
                placeholder={"Fees"}
                value={`${formatCurrency(formatNumber(strategy.fees), strategy.quote)}`}
              />
              <DottedTextField
                placeholder={"Realized profit "}
                value={`${formatCurrency(formatNumber(strategy.realizedPnl), strategy.quote)}`}
              />

              <PaperPosition
                valuePosition={strategy.currentOpenPositionAmount}
                base={strategy.base}
                avgPosition={strategy.avgPositionOpeningPrice}
                quote={strategy.quote}
                unrealizedPnl={strategy.unrealizedPnl}
              />
            </Stack>
          </PaperCollapse>

          <PaperCollapse label={"Strategy position"} collapsed={false}>
            <Stack width="100%" direction="column" gap="8px">
              <DottedTextField
                placeholder={"Fees"}
                value={`${formatCurrency(formatNumber(strategy.strategyPositionFees), strategy.quote)}`}
              />
              <DottedTextField
                placeholder={"Realized profit "}
                value={`${formatCurrency(formatNumber(strategy.strategyPositionRealizedPnl), strategy.quote)}`}
              />

              <PaperPosition
                valuePosition={strategy.currentStrategyPositionAmount}
                avgPosition={strategy.avgStrategyPositionPrice}
                base={strategy.base}
                quote={strategy.quote}
                unrealizedPnl={strategy.unrealizedStrategyPositionPnl}
              />
            </Stack>
          </PaperCollapse>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default AutoStrategyPerformanceInfo;
